import { useCallback } from 'react';
import { UseQueryExecute } from 'urql';

import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { PlusOutline } from '@lyearn/icons';
import { Loader, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { CourseLanguageInfoFragment } from '@/modules/corporate/modules/course/graphql/fragments/generated/CourseLanguageInfo';
import { useCreateLocalizedContentsMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/createLocalizedContents';
import { QuizLanguageInfoFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizLanguageInfo';
import { CourseContentType, LanguageEnum } from '@/types/schema';

import { getLanguageName } from '.';

type Props = {
  language: LanguageEnum;
  content: CourseLanguageInfoFragment | QuizLanguageInfoFragment;
  refetchQuery?: UseQueryExecute;
};
export const AddButton = ({ language, content, refetchQuery }: Props) => {
  const { t } = useTranslation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [{ fetching: isCreatingLocalizedContent }, createLocalizedContent] =
    useCreateLocalizedContentsMutation();
  const [isAdded, { on }] = useToggle();

  const onAdd = useCallback(async () => {
    const { isLocalizedContent, sourceContent, id } = content;

    await createLocalizedContent({
      input: {
        entityId: isLocalizedContent ? (sourceContent as CourseContentType)?.id || '' : id,
        languageTypes: [language],
      },
    }).then((res) => {
      if (res.data?.createLocalizedContents.languageConfigs[0]?.contentId?.length) {
        on();
        refetchQuery?.();
        const jobScheduleId = res.data?.createLocalizedContents.languageConfigs[0]?.jobScheduleId;
        showSnackbar({
          message: t('Course:CreateLocalizedContentSuccess', {
            language: getLanguageName(language),
          }),
        });
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    });
  }, [
    content,
    createLocalizedContent,
    language,
    on,
    showSnackbar,
    t,
    showErrorSnackbar,
    refetchQuery,
  ]);

  if (isAdded) {
    return null;
  }

  return (
    <Stack
      alignItems="center"
      className="cursor-pointer px-12 hover:bg-interactive-secondary-hover rounded-l-[9999px] rounded-r-[9999px] py-4 bg-bg-secondary"
      onClick={(event) => {
        if (isCreatingLocalizedContent) {
          return;
        }
        stopEventPropagation(event);
        onAdd();
      }}>
      {isCreatingLocalizedContent ? (
        <Loader className="mx-[9.45px]" size={18} />
      ) : (
        <>
          <PlusOutline className="h-12 w-12" />
          <Typography color="text-text-secondary" variant="label-03">
            {t('Common:Actions.Add')}
          </Typography>
        </>
      )}
    </Stack>
  );
};
